import { __rest } from 'tslib';
import React from 'react';
import { Checkbox } from '../../primitives/Checkbox/Checkbox.js';
import { Spinner } from '@10d/tend-ui-primitives';
import { Search } from '../Search/Search.js';
import { Box } from '@10d/tend-ui-grid';
import { useTheme } from '@10d/tend-ui-theme';
import { Scrollable } from '../../ui/Scrollable/Scrollable.js';
import { useFilterOption } from '../../hooks/useFilterOption.js';
import { useCallbackRef } from '@10d/tend-ui-hooks';
import { EmptyOverlay } from '../../ui/EmptyOverlay/EmptyOverlay.js';
import { ErrorOverlay } from '../../ui/ErrorOverlay/ErrorOverlay.js';

const CheckboxGroupSearch = (_a) => {
    var { optionAfter, placeholder, allowClear, error = false, loading = false, filterOption = true, filterOptionProp = 'value', scrollable = true, optionRender, options, onSearch, onChange, value, onScroll } = _a, props = __rest(_a, ["optionAfter", "placeholder", "allowClear", "error", "loading", "filterOption", "filterOptionProp", "scrollable", "optionRender", "options", "onSearch", "onChange", "value", "onScroll"]);
    const theme = useTheme();
    const [selectedValues, setSelectedValues] = React.useState([]);
    const [search, setSearch] = React.useState('');
    React.useEffect(() => {
        if (!value) {
            setSelectedValues([]);
            return;
        }
        setSelectedValues(value);
    }, [value]);
    const handleSearch = React.useCallback((e) => {
        onSearch === null || onSearch === void 0 ? void 0 : onSearch(e.target.value);
        setSearch(e.target.value);
    }, [onSearch]);
    const handleChange = React.useCallback(e => {
        // FIXME: Нормально ли так делать? Непонятно, но варианта вызывать onChange
        // не создавая side-effect пока не вижу
        let _next = [];
        setSelectedValues(previousSelectedValues => {
            const value = e.target.value;
            const checked = e.target.checked;
            if (checked) {
                const next = [...previousSelectedValues, value];
                _next = next;
                return next;
            }
            const next = previousSelectedValues.filter(pValue => pValue !== value);
            _next = next;
            return next;
        });
        onChange === null || onChange === void 0 ? void 0 : onChange(_next);
    }, [onChange]);
    const createLabel = useCallbackRef((node, option) => {
        if (!optionAfter)
            return node;
        if (React.isValidElement(optionAfter))
            return (React.createElement(Box, { "$display": 'flex', "$alignItems": 'center' },
                node,
                React.createElement(Box, { "$flex": '1', "$display": 'flex', "$justifyContent": 'flex-end' }, optionAfter)));
        if (typeof optionAfter === 'function')
            return (React.createElement(Box, { "$display": 'flex', "$alignItems": 'center' },
                node,
                React.createElement(Box, { "$flex": '1', "$display": 'flex', "$justifyContent": 'flex-end' }, optionAfter(option))));
        return node;
    });
    const _options = React.useMemo(() => {
        if (!options)
            return [];
        return options.map(item => (Object.assign(Object.assign({}, item), { label: optionRender
                ? createLabel(optionRender(item), item)
                : createLabel(item.label, item) })));
    }, [createLabel, optionRender, options]);
    const filteredOptions = useFilterOption({
        search,
        filterOption,
        options: _options,
        filterOptionProp,
    });
    const content = React.useMemo(() => {
        if (filteredOptions.length)
            return (React.createElement(Checkbox.Group, Object.assign({ fullWidth: true, layout: 'vertical' }, props, { value: selectedValues }), filteredOptions.map(filteredOption => (React.createElement(Checkbox, { key: typeof filteredOption.value !== 'boolean'
                    ? filteredOption.value
                    : filteredOption.id, value: filteredOption.value, onChange: handleChange }, filteredOption.label)))));
        if (loading)
            return null;
        if (error)
            return React.createElement(ErrorOverlay, null);
        if (!filteredOptions.length)
            return React.createElement(EmptyOverlay, null);
        return (React.createElement(Checkbox.Group, Object.assign({ fullWidth: true, layout: 'vertical' }, props, { value: selectedValues }), filteredOptions.map(filteredOption => (React.createElement(Checkbox, { key: typeof filteredOption.value !== 'boolean'
                ? filteredOption.value
                : filteredOption.id, value: filteredOption.value, onChange: handleChange }, filteredOption.label)))));
    }, [error, filteredOptions, handleChange, loading, props, selectedValues]);
    return (React.createElement(Spinner, { color: theme.colors.blue600, size: 'small', loading: loading },
        React.createElement(Box, { "$display": 'flex', "$flexDirection": 'column', "$gap": 8 },
            React.createElement(Search, { onChange: handleSearch, allowClear: allowClear, placeholder: placeholder }),
            scrollable ? React.createElement(Scrollable, { onScroll: onScroll }, content) : content)));
};
CheckboxGroupSearch.displayName = 'CheckboxGroupSearch';

export { CheckboxGroupSearch };
//# sourceMappingURL=CheckboxGroupSearch.js.map
